import React, { type PropsWithChildren, useRef } from 'react';
import { ApplicationView as view } from '@aha-app/mvc';
import { useDrop } from 'react-dnd';
import SortableItem, { type SortableItemProps } from './SortableItem';
import type DraggableItem from '../DraggableItem';
import { sharedDragController } from '../controllers/DragController';

const DroppableItem: React.FC<PropsWithChildren<SortableItemProps>> = ({
  children,
  dragging = false,
  dragItem,
  dropHandler,
  hideDragPreviews = true,
  listLayout = 'vertical',
  listKey,
  ItemComponent,
  draggableClassName = null,
}) => {
  const ref = useRef<HTMLDivElement>(null);

  const [, drop] = useDrop({
    accept: dropHandler?.accepts || [],
    hover(draggedItem: DraggableItem, monitor) {
      if (!dropHandler?.canHover({ draggedItem })) return;

      dropHandler.onReorder({
        draggedItem,
        placement: dropHandler.calculatePlacement({
          dragItem,
          listLayout,
          clientOffset: monitor.getClientOffset()!,
          hoverBoundingRect: ref.current!.getBoundingClientRect(),
        }),
      });
    },
    canDrop(draggedItem, monitor) {
      return dropHandler?.canHover({ draggedItem }) || false;
    },
    drop(draggedItem, monitor) {
      if (!monitor.didDrop()) {
        sharedDragController.actionFinishDrop();
      }
    },
  });

  drop(ref);

  return (
    <div
      className={draggableClassName ?? undefined}
      ref={ref}
      hidden={hideDragPreviews && dragging}
    >
      <SortableItem
        dragging={dragging}
        dragItem={dragItem}
        dropHandler={dropHandler}
        listKey={listKey}
        hideDragPreviews={hideDragPreviews}
        listLayout={listLayout}
        ItemComponent={ItemComponent}
      >
        {children}
      </SortableItem>
    </div>
  );
};

export default view(DroppableItem);
